import { Piano } from "../../piano";
import { MicrophoneInput } from "../microphone";
import { Observable, type Disposable, type ReadonlyObservable } from "@/lib/studio/observable";

const clamp0to100 = (v: number): number => Math.max(0, Math.min(100, v));

interface DetectionParams {
  silenceThreshold: number;
  clarityThreshold: number;
  stabilityFrames: number;
}

/**
 * Owns the microphone PianoInput lifecycle and the user-facing sensitivity
 * slider. The controller is the single place where the 0–100 UI value is
 * mapped to detection parameters, and where the live input is kept in sync
 * with that mapping.
 */
export class MicrophoneController implements Disposable {
  private _input: MicrophoneInput | null = null;

  private _active = new Observable<boolean>(false);
  private _sensitivity = new Observable<number>(50, { normalize: clamp0to100 });

  constructor(private readonly piano: Piano, private readonly ctx: AudioContext) {}

  get active(): ReadonlyObservable<boolean> {
    return this._active;
  }

  get sensitivity(): ReadonlyObservable<number> {
    return this._sensitivity;
  }

  attach(stream: MediaStream): MicrophoneInput {
    if (this._input) {
      return this._input;
    }

    const params = MicrophoneController.sensitivityToDetectionParams(this._sensitivity.value);
    const input = new MicrophoneInput(this.piano, this.ctx, {
      // En débogage, `MicrophoneInput` refait un `findPitch` sur une FFT de
      // 4096 toutes les trente trames pour le journal : pas en production.
      debug: process.env.NODE_ENV !== "production",
      silenceThreshold: params.silenceThreshold,
      clarityThreshold: params.clarityThreshold,
      stabilityFrames: params.stabilityFrames,
    });
    input.setStream(stream);
    input.attach();

    this._input = input;
    this._active.set(true);

    return input;
  }

  detach(): void {
    if (!this._input) {
      return;
    }

    this._input.detach();
    this._input = null;
    this._active.set(false);
  }

  getInput(): MicrophoneInput | null {
    return this._input;
  }

  setSensitivity(value: number): void {
    const clamped = clamp0to100(value);

    if (this._sensitivity.value === clamped) {
      return;
    }

    const params = MicrophoneController.sensitivityToDetectionParams(clamped);

    if (this._input) {
      this._input.setDetectionParams(params);
    }

    this._sensitivity.set(clamped);
  }

  dispose(): void {
    this.detach();
    this._active.dispose();
    this._sensitivity.dispose();
  }

  // ── Mic sensitivity (0–100, UI scale) ──
  //
  // The slider drives THREE detection parameters simultaneously, not just
  // the amplitude gate, mapping silenceThreshold alone leaves the slider
  // feeling weak past the midpoint because quiet notes get rejected by
  // pitch-clarity or stability gates well before the amplitude gate
  // matters. All three move together along a single coordinated curve.
  //
  //   sensitivity=0   → silence=0.030    clarity=0.92  stability=5  (deaf, very strict)
  //   sensitivity=25  → silence=0.0072   clarity=0.83  stability=4
  //   sensitivity=50  → silence=0.0017   clarity=0.74  stability=3  (mid)
  //   sensitivity=75  → silence=0.00041  clarity=0.64  stability=2
  //   sensitivity=100 → silence=0.0001   clarity=0.55  stability=1  (very permissive)
  //
  // Silence uses a LOGARITHMIC curve (each +25 on the slider divides the
  // amplitude gate by ~4×) so the slider feels evenly graduated, a linear
  // or low-exponent curve compresses most of the useful range into the top
  // 20%. Clarity and stability are linear ramps; in practice the silence
  // gate is the dominant perceptual lever.
  private static sensitivityToDetectionParams(sensitivity: number): DetectionParams {
    const clamped = clamp0to100(sensitivity);
    const s = clamped / 100;
    const inverted = 1 - s;

    return {
      silenceThreshold: 0.0001 * Math.pow(300, inverted),
      clarityThreshold: 0.55 + 0.37 * inverted,
      stabilityFrames: Math.max(1, Math.round(5 - 4 * s)),
    };
  }
}
